import { useEffect, useState } from 'react';
import {
  fetchExaminationByCustomer,
  selectCustomerExaminations,
} from '../../store/slices/examinations';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { ExaminationFrames } from '../../components/ExminationFrames/ExaminationFrames';
import { ExaminationAudio } from '../../components/ExaminationAudio/ExaminationAudio';
import WithLoader from '../../components/WithLoader/WithLoader';
import classes from './CustomerExaminations.module.scss';
import moment from 'moment';
import _ from 'lodash';
import { examinationContentImg, examinationFileType } from '../../utils/miscUtils';
import Collapse from '../VitalSignResultsV2/CollapseV2';
import BackButton from '../../components/UI/Buttons/BackButton';
import rightIcon from '../../assets/icons/rightIcon.svg';

import CustomPagination from '../../components/UI/CustomPagination/CustomPagination';
import { useTranslation } from '../../hooks/useTranslations';

export default function CustomerExaminations({
  accountNumber,
}: {
  accountNumber: string | undefined;
}) {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const lang = localStorage.getItem('lang') || 'en';
  const customerExaminations = useAppSelector(selectCustomerExaminations);
  const [showDetails, setShowDetails] = useState<{ groupName: string; groupId: number }>();
  const allExaminations = customerExaminations.flatMap((item) =>
    item.examinations.map((exam) => ({
      ...exam,
      result_date: item.result_date,
    }))
  );

  const sortedData = allExaminations.sort(
    (a, b) => new Date(b.result_date).getTime() - new Date(a.result_date).getTime()
  );
  const uniquedData = _.uniqBy(sortedData, 'examination_id');
  const filteredData = sortedData.filter((item) => item?.examination_id === showDetails?.groupId);
  const detailedData: Record<string, any[]> = _.groupBy(filteredData, (item) =>
    moment(item.result_date).locale(lang).format('DD MMMM YYYY')
  );

  useEffect(() => {
    if (accountNumber) dispatch(fetchExaminationByCustomer(accountNumber));
  }, []);

  const handleGroupClick = (groupName: string, groupId: number) => {
    setShowDetails({ groupName, groupId });
  };

  return (
    <WithLoader isLoading={false}>
      {uniquedData.length ? (
        <div>
          {showDetails ? (
            <div>
              <div className={classes.headerContainer}>
                <h2 className={classes.title}>{showDetails.groupName}</h2>
                <BackButton
                  style={{ width: 102, justifyContent: 'center', justifySelf: 'right', margin: 15 }}
                  onClick={() => setShowDetails(undefined)}
                />
              </div>
              <CustomPagination itemsPerPage={10} items={Object.entries(detailedData)}>
                {(paginatedItems: any[]) => {
                  return (
                    <div>
                      {paginatedItems.map(([groupName, results]) => {
                        return (
                          <Collapse title={groupName} isToggleOpen={false}>
                            {results.map((examination: any, index: number) => {
                              return (
                                <div key={index} className={classes.container}>
                                  <div className={classes.content}>
                                    <div>
                                      <span className={classes.time}>
                                        {moment(examination.result_date).format('HH:mm')}
                                      </span>
                                    </div>
                                    <ExaminationContent examination={examination} />
                                  </div>
                                </div>
                              );
                            })}
                          </Collapse>
                        );
                      })}
                    </div>
                  );
                }}
              </CustomPagination>
            </div>
          ) : (
            <div>
              {uniquedData.map((examination, index) => {
                return (
                  <div key={index} className={classes.container}>
                    <div
                      className={classes.clickableArea}
                      onClick={() =>
                        handleGroupClick(examination.examination_name, examination.examination_id)
                      }
                    >
                      <p className={classes.title}>{examination.examination_name}</p>
                      <div className={classes.date}>
                        <span className={classes.time}>
                          {moment(examination.result_date).format('DD.MM.YYYY, HH:mm')}
                        </span>
                        <img src={rightIcon} alt="right-icon" />
                      </div>
                    </div>
                    <div className={classes.content} style={{ marginBottom: '10px' }}>
                      <ExaminationContent examination={examination} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className={classes.emptyStateContainer}>
          <div className="emptyState">{translations('label.empty_state')}</div>
        </div>
      )}
    </WithLoader>
  );
}

const ExaminationContent = ({ examination }: { examination: any }) => {
  if (examinationFileType(examination) === 'image') {
    return (
      <ExaminationFrames
        frames={examination.attachments.map((attachment: any) => attachment.link) ?? []}
      />
    );
  } else {
    return (
      <ExaminationAudio
        image={examinationContentImg(examination)}
        attachments={examination.attachments.map((attachment: any) => attachment.link)}
      />
    );
  }
};
